import {
  Divider,
  Icon,
  PlainButton,
  Popover,
  PopoverContent,
  StatusIndicator,
  Text,
  Truncated,
} from '@customerio/pluma-components/react';
import { isSessionRunning } from '@customerio/hydra-copilot';
import type { CopilotSession, CopilotSessionStatus, RegistryEntry } from '@customerio/hydra-copilot';
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useMatch } from '@/services/router/compat';
import { useSidebarActive } from '@/features/sidebar';
import { useAnalytics } from '@/services/analytics';
import { sentryCaptureException } from '@/utils/sentry';
import { isEmptyString } from '@/utils/string';
import { useShouldShowAgentBar } from '../../hooks/use-agent-fullscreen';
import { useCopilotSidebar } from '../../hooks/use-copilot-sidebar';
import { useAgentConversations, type AgentConversationRow } from '../../hooks/use-agent-conversations';
import { useCopilotConversations, useCopilotSessionContext } from '../session-provider';
import { AskAgentSparkleIcon } from '../ask-agent-sparkle-icon';
import { useSuggestions } from '../suggestions';
import { formatCompactRelativeTime } from './menu-format';
import * as styles from './index.css';

const MAX_MENU_SUGGESTIONS = 3;

/** How long the launcher introduces itself as a labeled pill before minimizing. */
const INITIAL_REVEAL_MS = 3000;

/**
 * True for the launcher's first few visible seconds, then false (pill →
 * circle). The countdown only runs while the launcher shows — mounting hidden
 * (sidebar open, home page) must not burn the intro before anyone sees it.
 */
function useInitialReveal(isHidden: boolean) {
  const [isRevealed, setIsRevealed] = useState(true);

  useEffect(() => {
    if (isHidden || !isRevealed) return;
    const timer = window.setTimeout(() => setIsRevealed(false), INITIAL_REVEAL_MS);
    return () => window.clearTimeout(timer);
  }, [isHidden, isRevealed]);

  return isRevealed;
}

/** Live status per registered session, updated on any session's transition. */
function useSessionStatuses(entries: ReadonlyArray<RegistryEntry>) {
  const [statuses, setStatuses] = useState<ReadonlyMap<CopilotSession, CopilotSessionStatus>>(new Map());

  useEffect(() => {
    const recompute = () => {
      setStatuses(new Map(entries.map((entry) => [entry.session, entry.session.__status.get()])));
    };
    recompute();
    const unsubscribes = entries.map((entry) => entry.session.__status.listen(recompute));
    return () => {
      for (const unsubscribe of unsubscribes) unsubscribe();
    };
  }, [entries]);

  return statuses;
}

/**
 * Replies that finished (running → ready) and that the user hasn't opened
 * yet. `viewedSession` is the conversation the user is currently reading (in
 * the open copilot sidebar or the fullscreen agent view, null otherwise): its
 * replies arrive read, while a background conversation's reply is unseen even
 * while the launcher itself is hidden.
 * Two levels of acknowledgement: `acknowledgePill` quiets the launcher's
 * announcement (any interaction), `clearUnread` clears a conversation's
 * unread state (opening that conversation).
 */
function useUnseenReplies(entries: ReadonlyArray<RegistryEntry>, viewedSession: CopilotSession | null) {
  // Session → epoch ms of its latest unseen reply.
  const [unreadBySession, setUnreadBySession] = useState<ReadonlyMap<CopilotSession, number>>(new Map());
  const [pillSession, setPillSession] = useState<CopilotSession | null>(null);
  const previousStatuses = useRef(new Map<string, CopilotSessionStatus>());
  const viewedSessionRef = useRef(viewedSession);
  viewedSessionRef.current = viewedSession;

  useEffect(() => {
    // Prune state for sessions the registry evicted, so we don't pin them.
    const liveSessions = new Set<CopilotSession>(entries.map((entry) => entry.session));
    setUnreadBySession((current) => {
      if ([...current.keys()].every((session) => liveSessions.has(session))) return current;
      return new Map([...current].filter(([session]) => liveSessions.has(session)));
    });
    setPillSession((current) => (current != null && !liveSessions.has(current) ? null : current));
    const liveKeys = new Set(entries.map((entry) => entry.key));
    for (const key of [...previousStatuses.current.keys()]) {
      if (!liveKeys.has(key)) previousStatuses.current.delete(key);
    }

    const unsubscribes = entries.map((entry) => {
      if (!previousStatuses.current.has(entry.key)) {
        previousStatuses.current.set(entry.key, entry.session.__status.get());
      }
      return entry.session.__status.listen((status) => {
        const previous = previousStatuses.current.get(entry.key);
        previousStatuses.current.set(entry.key, status);
        if (
          previous != null &&
          isSessionRunning(previous) &&
          status === 'ready' &&
          entry.session !== viewedSessionRef.current
        ) {
          setUnreadBySession((current) => {
            const next = new Map(current);
            next.set(entry.session, Date.now());
            return next;
          });
          setPillSession(entry.session);
        }
      });
    });

    return () => {
      for (const unsubscribe of unsubscribes) unsubscribe();
    };
  }, [entries]);

  const clearUnread = (session: CopilotSession) => {
    setUnreadBySession((current) => {
      if (!current.has(session)) return current;
      const next = new Map(current);
      next.delete(session);
      return next;
    });
    if (pillSession === session) setPillSession(null);
  };

  return { unreadBySession, pillSession, acknowledgePill: () => setPillSession(null), clearUnread };
}

type SectionHeaderProps = { children: React.ReactNode };

function SectionHeader({ children }: SectionHeaderProps) {
  return <div className={styles.sectionHeader}>{children}</div>;
}

type ConversationRowProps = {
  row: AgentConversationRow;
  status: CopilotSessionStatus | undefined;
  /** Epoch ms of the row's unseen reply, if any. */
  unreadAt: number | undefined;
  onSelect: (row: AgentConversationRow) => void;
};

function ConversationRow({ row, status, unreadAt, onSelect }: ConversationRowProps) {
  const isRunning = isSessionRunning(status);
  const isBusy = isRunning || status === 'initializing';
  const isUnread = unreadAt != null;
  const label = !isEmptyString(row.name) ? row.name : `Chat ${row.id}`;

  return (
    <PlainButton className={styles.menuRow} onClick={() => onSelect(row)}>
      {isBusy ? (
        <span className={styles.rowIndicator}>
          <StatusIndicator color="green" isPulsing aria-label="The Agent is working" />
        </span>
      ) : isUnread ? (
        <span className={styles.rowIndicator}>
          <span className={styles.unreadDot} role="img" aria-label="New reply" />
        </span>
      ) : null}
      <span className={styles.rowLabel}>
        <Text
          size="sm"
          weight={isBusy || isUnread ? 'semibold' : 'regular'}
          color={isBusy || isUnread ? 'base' : 'subtle'}
          display="block"
          minW="0"
        >
          <Truncated cursor="pointer">{label}</Truncated>
        </Text>
        {isRunning ? (
          <Text size="xs" color="success" display="block">
            Running…
          </Text>
        ) : isUnread ? (
          <Text size="xs" color="subtle" display="block">
            The Agent replied {formatCompactRelativeTime(Math.floor(unreadAt / 1000))}
          </Text>
        ) : null}
      </span>
      <span className={styles.rowStatus}>
        {!isBusy && !isUnread && row.updatedAt != null ? (
          <Text size="xs" color="subtle" whiteSpace="nowrap">
            {formatCompactRelativeTime(row.updatedAt)}
          </Text>
        ) : null}
      </span>
    </PlainButton>
  );
}

export function AgentFab() {
  const isCopilotSidebarOpen = useSidebarActive('copilot');
  const isHistorySidebarOpen = useSidebarActive('copilot-history');
  const shouldShowAgentBar = useShouldShowAgentBar();
  const isHomePage = useMatch('/workspaces/:workspaceId/home') !== null;
  const { track } = useAnalytics();
  const { openCopilot } = useCopilotSidebar();
  const session = useCopilotSessionContext();
  const { newConversation, switchToConversation, entries } = useCopilotConversations();
  const { rows } = useAgentConversations();
  const suggestionState = useSuggestions(session);
  const statuses = useSessionStatuses(entries);
  const isHidden = isCopilotSidebarOpen || isHistorySidebarOpen || !shouldShowAgentBar || isHomePage;
  // The conversation the user is currently reading: the active session while
  // the copilot sidebar or the fullscreen agent view shows it.
  const viewedSession = isCopilotSidebarOpen || !shouldShowAgentBar ? session : null;
  const { unreadBySession, pillSession, acknowledgePill, clearUnread } = useUnseenReplies(entries, viewedSession);
  const isInitialReveal = useInitialReveal(isHidden);

  const isAnyRunning = [...statuses.values()].some(isSessionRunning);
  const hasUnseenReply = pillSession != null;
  const finishedName =
    pillSession != null ? (rows.find((row) => row.liveSession === pillSession)?.name ?? 'Reply ready') : null;

  if (isHidden) {
    return null;
  }

  const suggestions = suggestionState.isVisible ? suggestionState.suggestions.slice(0, MAX_MENU_SUGGESTIONS) : [];

  const isRowActive = (row: AgentConversationRow) => {
    if (row.liveSession == null) return false;
    const status = statuses.get(row.liveSession);
    return isSessionRunning(status) || status === 'initializing' || unreadBySession.has(row.liveSession);
  };
  const activeRows = rows.filter(isRowActive);
  const recentRows = rows.filter((row) => !isRowActive(row));

  const handleOpen = () => {
    acknowledgePill();
    if (session.id != null) {
      // Opening the launcher lands in the active conversation; its reply is read.
      const activeEntry = entries.find((entry) => entry.session.id === session.id);
      if (activeEntry != null) clearUnread(activeEntry.session);
    }
    openCopilot({ triggerSource: 'fab' });
  };

  const handleNewConversation = () => {
    newConversation();
    track('aiAssistant', 'newChatClicked', { 'Click Location': 'fab' });
    openCopilot({ triggerSource: 'fab_new_conversation' });
  };

  const handleSelectConversation = (row: AgentConversationRow) => {
    track('aiAssistant', 'historyItemClicked', { 'Click Location': 'fab' });
    const opened = switchToConversation(row.id);
    clearUnread(opened);
    openCopilot({ triggerSource: 'fab_conversation' });
  };

  const handleSuggestionClick = async (message: string, index: number, label: string, group: string | undefined) => {
    try {
      openCopilot({ triggerSource: 'fab_suggestion' });
      await session.sendMessage(message);
      track('aiAssistant', 'suggestionClicked', {
        Index: index,
        'Click Location': 'fab',
        'Conversation ID': session.id,
        'Suggestion Label': label,
        'Suggestion Group': group ?? 'none',
        'Route Group': suggestionState.routeGroup,
        'Suggestion Source': suggestionState.source,
      });
    } catch (error) {
      sentryCaptureException(error);
    }
  };

  return (
    <div className={styles.fabContainer}>
      <Popover
        // Hover-open is intentional (Popover guidance prefers click): hovering
        // previews the conversation menu, while clicking the launcher goes
        // straight to the sidebar — so click can't also toggle the menu.
        shouldTriggerOnClick={false}
        shouldTriggerOnHover
        shouldTriggerOnFocus
        // Keep focus on the launcher when the menu hover-opens, so the first
        // row doesn't paint a focus ring; Tab still reaches the rows.
        initialFocus={-1}
        placement="top-end"
        onOpenChange={(isOpen) => {
          if (isOpen) acknowledgePill();
        }}
      >
        {(context) => (
          <>
            <PlainButton
              ref={context.reference.setReference}
              {...context.reference.getReferenceProps()}
              aria-label="Ask the Agent"
              data-unseen-reply={hasUnseenReply ? 'true' : undefined}
              data-working={isAnyRunning ? 'true' : undefined}
              className={clsx(
                styles.fabButton,
                (isInitialReveal || hasUnseenReply) && styles.fabButtonExpanded,
                hasUnseenReply && styles.fabButtonPulsing,
                isAnyRunning && styles.fabButtonWorking,
              )}
              onClick={handleOpen}
            >
              <AskAgentSparkleIcon variant={isAnyRunning ? 'gradient' : 'inverse'} />
              <span className={clsx(styles.fabLabel, hasUnseenReply && styles.fabLabelPulsing)}>
                {finishedName ?? 'Ask the Agent'}
              </span>
            </PlainButton>
            <PopoverContent>
              <div className={styles.menu}>
                <PlainButton className={styles.menuRow} onClick={handleNewConversation}>
                  <Icon name="add" size="sm" color="base" />
                  <Text size="sm" weight="semibold">
                    Start conversation
                  </Text>
                </PlainButton>
                {suggestions.length > 0 ? (
                  <>
                    <Divider my="075" />
                    <SectionHeader>Suggested</SectionHeader>
                    {suggestions.map((suggestion, index) => (
                      <PlainButton
                        key={suggestion.label}
                        className={styles.menuRow}
                        onClick={() =>
                          void handleSuggestionClick(suggestion.message, index + 1, suggestion.label, suggestion.group)
                        }
                      >
                        <Icon name="sparkle" size="sm" color="accent" />
                        <span className={styles.rowLabel}>
                          <Text size="sm" display="block" minW="0">
                            <Truncated cursor="pointer">{suggestion.label}</Truncated>
                          </Text>
                        </span>
                      </PlainButton>
                    ))}
                  </>
                ) : null}
                {activeRows.length > 0 ? (
                  <>
                    <Divider my="075" />
                    <SectionHeader>Active now</SectionHeader>
                    {activeRows.map((row) => (
                      <ConversationRow
                        key={row.id}
                        row={row}
                        status={row.liveSession != null ? statuses.get(row.liveSession) : undefined}
                        unreadAt={row.liveSession != null ? unreadBySession.get(row.liveSession) : undefined}
                        onSelect={handleSelectConversation}
                      />
                    ))}
                  </>
                ) : null}
                {recentRows.length > 0 ? (
                  <>
                    <Divider my="075" />
                    <SectionHeader>Recent</SectionHeader>
                    {recentRows.map((row) => (
                      <ConversationRow
                        key={row.id}
                        row={row}
                        status={undefined}
                        unreadAt={undefined}
                        onSelect={handleSelectConversation}
                      />
                    ))}
                  </>
                ) : null}
              </div>
            </PopoverContent>
          </>
        )}
      </Popover>
    </div>
  );
}
